import { useState } from 'react';
import type { ProviderMatch, MatchLevel } from './matchingTypes';
import type { Resource } from '../types';
import { UnmatchedCriteriaTooltip } from './UnmatchedCriteriaTooltip';

// ─── Match level → visual tokens ──────────────────────────────────────────

const LEVEL_STYLES: Record<MatchLevel, { border: string; badge: string; icon: string }> = {
  strong:  { border: '#22c55e', badge: '#dcfce7', icon: '●' },
  partial: { border: '#f59e0b', badge: '#fef3c7', icon: '◐' },
  weak:    { border: '#cbd5e1', badge: '#f1f5f9', icon: '○' },
};

const LEVEL_LABEL: Record<MatchLevel, string> = {
  strong:  'Strong match',
  partial: 'Partial match',
  weak:    'Weak match',
};

// ─── Next-available formatter ──────────────────────────────────────────────

function formatNextAvailable(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Component ────────────────────────────────────────────────────────────

interface Props {
  resource: Resource;
  match: ProviderMatch;
  isVisible: boolean;
  onProviderClick: (resourceId: string) => void;
}

export function MatchedResourceCard({ resource, match, isVisible, onProviderClick }: Props) {
  const [hovered, setHovered] = useState(false);
  const tokens = LEVEL_STYLES[match.matchLevel];

  return (
    <button
      onClick={() => onProviderClick(resource.id)}
      title={`${resource.name} — ${LEVEL_LABEL[match.matchLevel]} (score ${match.matchScore})`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '6px 8px 6px 8px',
        gap: 3,
        background: isVisible ? '#eff6ff' : hovered ? '#f8fafc' : 'transparent',
        border: 'none',
        borderBottom: '1px solid #f1f5f9',
        borderLeft: `3px solid ${tokens.border}`,
        cursor: 'pointer',
        textAlign: 'left',
        boxSizing: 'border-box',
        transition: 'background 0.1s',
      }}
    >
      {/* ── Row 1: color dot + name + badge + score + unmatched tooltip ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: resource.color ?? '#64748b',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            flex: 1,
            fontSize: 12,
            color: isVisible ? '#1d4ed8' : '#374151',
            fontWeight: isVisible ? 600 : 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {resource.name}
        </span>

        {/* Score */}
        <span
          style={{
            fontSize: 10,
            color: '#64748b',
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
          title={`Match score: ${match.matchScore}`}
        >
          {match.matchScore}
        </span>

        {/* Match level icon */}
        <span
          style={{ fontSize: 10, color: tokens.border, flexShrink: 0 }}
          title={LEVEL_LABEL[match.matchLevel]}
        >
          {tokens.icon}
        </span>

        {/* Unmatched criteria tooltip */}
        {match.unmatchedCriteria.length > 0 && (
          <UnmatchedCriteriaTooltip unmatchedCriteria={match.unmatchedCriteria} />
        )}
      </div>

      {/* ── Badge label ── */}
      {match.badgeLabel && (
        <div style={{ paddingLeft: 13 }}>
          <span
            style={{
              display: 'inline-block',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              padding: '1px 6px',
              borderRadius: 10,
              background: tokens.badge,
              color: tokens.border,
              border: `1px solid ${tokens.border}`,
            }}
          >
            {match.badgeLabel}
          </span>
        </div>
      )}

      {/* ── Matched criteria tags ── */}
      {match.matchedCriteria.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, paddingLeft: 13 }}>
          {match.matchedCriteria.map((c) => (
            <span
              key={c}
              style={{
                fontSize: 9,
                padding: '1px 5px',
                borderRadius: 8,
                background: '#f1f5f9',
                color: '#475569',
                border: '1px solid #e2e8f0',
                whiteSpace: 'nowrap',
              }}
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {/* ── Next available ── */}
      {match.nextAvailable && (
        <div
          style={{
            fontSize: 10,
            color: '#64748b',
            paddingLeft: 13,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Next open: {formatNextAvailable(match.nextAvailable)}
        </div>
      )}
    </button>
  );
}
